import { Icon } from "materia"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { type ImageDraft, uploadNote } from "./imageDraft"
import s from "./ComposerImage.module.scss"

type ComposerImageProps = {
  /** A local object URL for the picked file. */
  previewUrl: string
  draft: ImageDraft
  onRemove: () => void
  onRetry: () => void
}

/** The picked image above the composer's field: its upload, and what came of it. */
export function ComposerImage({
  previewUrl,
  draft,
  onRemove,
  onRetry,
}: ComposerImageProps) {
  const { t } = useTranslation()
  // A browser that cannot decode the file (HEIC, mostly) gets an icon instead.
  const [canPreview, setCanPreview] = useState(true)
  const note = uploadNote(draft)

  return (
    <div className={modifiers(s, "image", { hasError: Boolean(note) })}>
      <div className={s.image__thumb}>
        {canPreview ? (
          <img
            className={s.image__preview}
            src={previewUrl}
            alt=""
            onError={() => setCanPreview(false)}
          />
        ) : (
          <Icon icon="Attachment" />
        )}
        {draft.phase === "uploading" && (
          <div
            className={s.image__progress}
            role="progressbar"
            aria-label={String(t("chat.IMAGE_UPLOADING_ARIA"))}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(draft.progress * 100)}
          >
            <span
              className={s.image__progressBar}
              style={{ width: `${draft.progress * 100}%` }}
            />
          </div>
        )}
      </div>

      {note && (
        <p className={s.image__note} role="alert">
          {t(note.key, { count: note.count })}
          {note.canRetry && (
            <button type="button" className={s.image__retry} onClick={onRetry}>
              {t("chat.MESSAGE_RETRY")}
            </button>
          )}
        </p>
      )}

      <button
        type="button"
        className={s.image__remove}
        onClick={onRemove}
        aria-label={String(t("chat.IMAGE_REMOVE_ARIA"))}
      >
        <Icon icon="Close" size="small" />
      </button>
    </div>
  )
}
